import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Video, ExternalLink, Smartphone, Users, Play, Mail, Phone, MapPin, Instagram, Twitter, Facebook } from "lucide-react";
import { useSiteContentCache, SiteLoadingScreen } from "@/contexts/SiteContentCache";

const ReeplayerPage = () => {
  const { data: cachedContent, loading: cacheLoading } = useSiteContentCache();

  if (!cachedContent && cacheLoading) return <SiteLoadingScreen />;

  const logoUrl = cachedContent?.logo_url || "";
  const footerData = cachedContent?.footer || {};
  const content = {
    title: "LIVE STREAMS",
    subtitle: "Watch Commonwealth FC in action",
    description: "Follow our games and training sessions live through Reeplayer. Join the groups below in the app to get notified when we go live.",
    reeplayer_links: [],
    ...(cachedContent?.reeplayer || {}),
  };

  const displayLinks = content.reeplayer_links || [];

  return (
    <>
      <Helmet>
        <title>Live Streams | Commonwealth FC</title>
        <meta name="description" content="Watch Commonwealth FC games and training live on Reeplayer." />
      </Helmet>

      <div className="min-h-screen bg-[#0C2E3C]">
        {/* Nav */}
        <nav className="bg-[#0C2E3C] border-b border-[#F0F4F3]/10 sticky top-0 z-50">
          <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" data-testid="reeplayer-home-link">
              <img src={logoUrl} alt="Commonwealth FC" className="h-10 w-auto" />
            </Link>
            <Link to="/join">
              <Button className="bg-[#C8E64E] text-[#0C2E3C] hover:bg-[#C8E64E]/90 font-bold text-xs uppercase tracking-wider px-6 h-10 rounded-none">
                Join the Club
              </Button>
            </Link>
          </div>
        </nav>

        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[#0E6072]/30 to-[#0C2E3C]" />
          <div className="relative z-10 max-w-4xl mx-auto px-6 pt-20 pb-12 text-center">
            <div className="inline-flex items-center gap-2 bg-[#C8E64E]/10 border border-[#C8E64E]/20 rounded-full px-4 py-1.5 mb-6">
              <Video size={14} className="text-[#C8E64E]" />
              <span className="text-[#C8E64E] text-xs font-semibold uppercase tracking-wider">Powered by Reeplayer</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#F0F4F3] mb-4" data-testid="reeplayer-title">
              {content.title}
            </h1>
            <p className="text-lg text-[#F0F4F3]/70 mb-3">{content.subtitle}</p>
            <p className="text-sm text-[#F0F4F3]/50 max-w-2xl mx-auto leading-relaxed">{content.description}</p>
          </div>
        </section>

        {/* How it works */}
        <section className="max-w-4xl mx-auto px-6 pb-10">
          <div className="grid sm:grid-cols-3 gap-4 mb-12">
            {[
              { icon: Smartphone, title: "Download Reeplayer", desc: "Get the app from the App Store or Google Play" },
              { icon: Users, title: "Join Our Groups", desc: "Tap the links below to follow CFC in the app" },
              { icon: Play, title: "Watch Live", desc: "Get notified when games start and watch live" },
            ].map((step) => (
              <div key={step.title} className="text-center p-6 border border-[#F0F4F3]/10 rounded-sm">
                <step.icon size={28} className="mx-auto mb-3 text-[#1FE3A8]" />
                <h3 className="text-sm font-bold text-[#F0F4F3] mb-1">{step.title}</h3>
                <p className="text-xs text-[#F0F4F3]/50">{step.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Stream Links */}
        <section className="max-w-4xl mx-auto px-6 pb-20" data-testid="reeplayer-links">
          <h2 className="font-heading text-2xl text-[#C8E64E] mb-6 text-center">Join Our Channels</h2>
          {displayLinks.length > 0 ? (
            <div className="grid sm:grid-cols-2 gap-4">
              {displayLinks.map((link) => (
                <a
                  key={link.id || link.label}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block"
                  data-testid={`reeplayer-link-${link.id || link.label}`}
                >
                  <Card className="border-[#F0F4F3]/10 bg-[#F0F4F3]/[0.03] hover:bg-[#F0F4F3]/[0.07] hover:border-[#C8E64E]/30 transition-all">
                    <CardContent className="p-5 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-[#C8E64E]/10 flex items-center justify-center shrink-0 group-hover:bg-[#C8E64E]/20 transition-colors">
                        <Video size={22} className="text-[#C8E64E]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-[#F0F4F3] font-semibold text-sm group-hover:text-[#C8E64E] transition-colors">{link.label}</h3>
                        {link.description && (
                          <p className="text-xs text-[#F0F4F3]/40 mt-0.5">{link.description}</p>
                        )}
                      </div>
                      <ExternalLink size={16} className="text-[#F0F4F3]/20 group-hover:text-[#C8E64E] transition-colors shrink-0" />
                    </CardContent>
                  </Card>
                </a>
              ))}
            </div>
          ) : (
            <div className="text-center py-10 border border-[#F0F4F3]/10 rounded-sm">
              <Video size={32} className="mx-auto mb-3 text-[#F0F4F3]/20" />
              <p className="text-[#F0F4F3]/40 text-sm">No streams available right now. Check back soon!</p>
            </div>
          )}

          {/* App download CTAs */}
          <div className="mt-10 text-center">
            <p className="text-xs text-[#F0F4F3]/40 mb-4">Don{"'"}t have Reeplayer yet?</p>
            <div className="flex items-center justify-center gap-4">
              <a
                href="https://apps.apple.com/us/app/reeplayer/id1588009700"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#F0F4F3]/10 hover:bg-[#F0F4F3]/15 text-[#F0F4F3] text-xs font-medium px-5 py-2.5 rounded-sm transition-colors"
                data-testid="reeplayer-appstore-link"
              >
                App Store
                <ExternalLink size={12} />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.reeplayer.android&hl=en_US"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#F0F4F3]/10 hover:bg-[#F0F4F3]/15 text-[#F0F4F3] text-xs font-medium px-5 py-2.5 rounded-sm transition-colors"
                data-testid="reeplayer-playstore-link"
              >
                Google Play
                <ExternalLink size={12} />
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-[#F0F4F3]/10 py-8" data-testid="livestream-footer">
          <div className="max-w-4xl mx-auto px-6">
            <div className="grid md:grid-cols-4 gap-8">
              <div className="md:col-span-2">
                <div className="flex items-center gap-2 mb-3">
                  <img src={logoUrl} alt="Commonwealth FC" className="h-10 w-auto object-contain" />
                  <div>
                    <h3 className="font-bold text-lg text-[#F0F4F3]">Commonwealth FC</h3>
                    <p className="text-[#F0F4F3]/50 text-sm mt-0.5">BUILT DIFFERENT. BUILT TOGETHER.</p>
                  </div>
                </div>
                <div className="flex gap-4 mt-3">
                  {footerData.instagram && <a href={footerData.instagram} target="_blank" rel="noopener noreferrer" className="text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors" aria-label="Instagram"><Instagram size={20} /></a>}
                  {footerData.twitter && <a href={footerData.twitter} target="_blank" rel="noopener noreferrer" className="text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors" aria-label="Twitter"><Twitter size={20} /></a>}
                  {footerData.facebook && <a href={footerData.facebook} target="_blank" rel="noopener noreferrer" className="text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors" aria-label="Facebook"><Facebook size={20} /></a>}
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-sm uppercase tracking-wider text-[#F0F4F3] mb-4">Contact</h4>
                <div className="space-y-3 text-sm">
                  {footerData.email && <a href={`mailto:${footerData.email}`} className="flex items-center gap-2 text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors"><Mail size={16} />{footerData.email}</a>}
                  {footerData.phone && <a href={`tel:${footerData.phone}`} className="flex items-center gap-2 text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors"><Phone size={16} />{footerData.phone}</a>}
                  {footerData.location && <p className="flex items-center gap-2 text-[#F0F4F3]/60"><MapPin size={16} />{footerData.location}</p>}
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-sm uppercase tracking-wider text-[#F0F4F3] mb-4">Quick Links</h4>
                <div className="space-y-2 text-sm">
                  <Link to="/" className="block text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors">Home</Link>
                  <Link to="/join" className="block text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors">Register</Link>
                  <Link to="/coaches" className="block text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors">Bring Your Team</Link>
                  <Link to="/livestream" className="block text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors">Live Streams</Link>
                </div>
              </div>
            </div>
            <div className="border-t border-[#F0F4F3]/10 mt-6 pt-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-[#F0F4F3]/40 text-sm">
              <span>&copy; {new Date().getFullYear()} Commonwealth FC. All rights reserved.</span>
              <div className="flex items-center gap-4">
                <Link to="/privacy" className="hover:text-[#C8E64E] transition-colors">Privacy Policy</Link>
                <Link to="/terms" className="hover:text-[#C8E64E] transition-colors">Terms</Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ReeplayerPage;
